import { useLocalization } from '~/composables/_next/useLocalization';
import type { ProductVariant } from '@az/middleware/src/__generated__/gql/graphql';

/**
 * Extracts and formats product attributes for one or multiple products.
 *
 * @param {ProductVariant | ProductVariant[]} products - Single product or list of products
 * @param {string[]} attributes - List of attribute names to filter
 * @returns {Record<string, any>} - Formatted attributes object
 */
export function getProductAttributes(products: ProductVariant | ProductVariant[], attributes: string[]) {
  const { locale } = useLocalization();
  const isSingleProduct = !Array.isArray(products);
  const productList = isSingleProduct ? [products] : products;

  if (!products || productList.length === 0) {
    return {};
  }

  const formatAttributes = (product: ProductVariant) => {
    return (
      product.attributesRaw
        ?.filter((attribute) => attributes.includes(attribute.name))
        .map((attribute) => {
          const value = attribute?.value?.key ?? attribute?.value;
          const label = attribute?.value?.label?.[locale] ?? value;
          return {
            name: attribute.name,
            value,
            label,
          };
        }) || []
    );
  };

  const reduceToUniqueAttributes = (prev: any[], curr: any) => {
    const exists = prev.some((item) => item.name === curr.name && item.value === curr.value);
    if (!exists) {
      prev.push(curr);
    }
    return prev;
  };

  const reduceByAttributeName = (prev: Record<string, any>, curr: any) => {
    if (isSingleProduct) {
      prev[curr.name] = curr.value;
    } else {
      prev[curr.name] = prev[curr.name] ?? [];
      prev[curr.name].push({
        value: curr.value,
        label: curr.label,
      });
    }
    return prev;
  };

  return productList
    .map((product) => formatAttributes(product))
    .flat()
    .reduce(reduceToUniqueAttributes, [])
    .reduce(reduceByAttributeName, {});
}
